import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import streamSaver from "streamsaver";
import { WritableStream } from "web-streams-polyfill/ponyfill";

// MUI
import { makeStyles } from "@mui/styles";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";

// Icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LockIcon from "@mui/icons-material/Lock";

// My Files
import firebase from "../../firebase";
import SimpleNavBar from "../navbar/SimpleNavBar";
import ToolbarWithLogo from "../ToolbarWithLogo";

// Self-host StreamSaver service worker for reliability
streamSaver.mitm = "/mitm.html";

// Detect Safari browser
const isSafari = () => {
    const ua = navigator.userAgent.toLowerCase();
    return (
        ua.includes("safari") &&
        !ua.includes("chrome") &&
        !ua.includes("chromium")
    );
};

const useStyles = makeStyles((theme) => ({
    container: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 128px)",
        padding: theme.spacing(2),
    },
    paper: {
        padding: theme.spacing(4),
        maxWidth: 450,
        width: "100%",
        textAlign: "center",
    },
    icon: {
        fontSize: 48,
        marginBottom: theme.spacing(2),
    },
    errorIcon: {
        fontSize: 48,
        marginBottom: theme.spacing(2),
        color: theme.palette.error.main,
    },
    fileName: {
        marginBottom: theme.spacing(1),
        wordBreak: "break-word",
    },
    expiryText: {
        marginBottom: theme.spacing(3),
        color: theme.palette.text.secondary,
    },
    passwordField: {
        marginBottom: theme.spacing(3),
    },
    downloadButton: {
        marginTop: theme.spacing(2),
    },
    progressContainer: {
        marginTop: theme.spacing(3),
        width: "100%",
    },
    warningText: {
        marginTop: theme.spacing(2),
        color: theme.palette.warning.main,
        fontSize: "0.85rem",
    },
}));

export default function SharePage() {
    const classes = useStyles();
    const location = useLocation();

    // State
    const [loading, setLoading] = useState(true);
    const [shareLink, setShareLink] = useState(null);
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");

    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const [downloading, setDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [isLargeFile, setIsLargeFile] = useState(false);
    const [safariWarning, setSafariWarning] = useState(false);

    // Extract link ID from URL
    const linkId = location.pathname.replace("/share/", "");

    // Helper function to check if link never expires
    function isNeverExpires(expiresAt) {
        const ninetyYearsFromNow = dayjs().add(90, "year");
        return dayjs(expiresAt).isAfter(ninetyYearsFromNow);
    }

    useEffect(() => {
        async function fetchShareLink() {
            try {
                const snapshot = await firebase
                    .database()
                    .ref("share_links")
                    .child(linkId)
                    .once("value");

                const data = snapshot.val();

                if (!data) {
                    setError("not_found");
                    setLoading(false);
                    return;
                }

                // Check if expired
                const now = dayjs();
                const expiresAt = dayjs(data.expiresAt);

                if (now.isAfter(expiresAt)) {
                    setError("expired");
                    setLoading(false);
                    return;
                }

                setShareLink(data);
                setLoading(false);
            } catch (err) {
                console.error("Error fetching share link:", err);
                setError("fetch_error");
                setLoading(false);
            }
        }

        if (linkId) {
            fetchShareLink();
        } else {
            setError("not_found");
            setLoading(false);
        }
    }, [linkId]);

    async function handleDownload() {
        setPasswordError("");
        setError(null);
        setErrorMessage("");
        setDownloading(true);
        setDownloadProgress(0);
        setIsLargeFile(false);
        setSafariWarning(false);

        // Warn user if they try to close tab during download
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue =
                "Download in progress. Are you sure you want to leave?";
            return e.returnValue;
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        try {
            // Call the Cloud Function to validate and get download URL
            const validateShareLink = firebase
                .functions()
                .httpsCallable("validateShareLink");

            const result = await validateShareLink({
                linkId: linkId,
                password: shareLink.hasPassword ? password : null,
            });

            const { downloadUrl, fileName } = result.data;

            // Safari - check file size and either use blob download or warn and attempt StreamSaver
            if (isSafari()) {
                try {
                    // First, get the file size with a HEAD request
                    const headResponse = await fetch(downloadUrl, {
                        method: "HEAD",
                    });
                    const contentLength =
                        headResponse.headers.get("content-length");
                    const fileSize = contentLength
                        ? parseInt(contentLength, 10)
                        : 0;

                    const SAFARI_BLOB_LIMIT = 500 * 1024 * 1024; // 500MB

                    if (fileSize > 0 && fileSize < SAFARI_BLOB_LIMIT) {
                        // Small enough for blob download
                        const response = await fetch(downloadUrl);

                        if (!response.ok) {
                            throw new Error(`Server error: ${response.status}`);
                        }

                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);

                        const link = document.createElement("a");
                        link.href = blobUrl;
                        link.download = fileName;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        URL.revokeObjectURL(blobUrl);

                        setDownloading(false);
                        setDownloadProgress(100);
                        window.removeEventListener(
                            "beforeunload",
                            handleBeforeUnload,
                        );
                        return;
                    } else {
                        // Large file - warn but continue with StreamSaver attempt
                        setSafariWarning(true);
                        // Fall through to StreamSaver code below
                    }
                } catch (safariError) {
                    console.error("Safari file size check error:", safariError);
                    // Fall through to StreamSaver attempt
                }
            }

            // Chrome/Firefox/Edge (and Safari fallback for large files) - use StreamSaver
            if (!window.WritableStream) {
                streamSaver.WritableStream = WritableStream;
                window.WritableStream = WritableStream;
            }

            const fileStream = streamSaver.createWriteStream(fileName, {});

            const response = await fetch(downloadUrl);

            if (!response.ok) {
                throw new Error(`Server error: ${response.status}`);
            }

            const contentLength = response.headers.get("content-length");
            const total = contentLength ? parseInt(contentLength, 10) : 0;
            let loaded = 0;

            // Show warning for large files (over 100MB) - but not for Safari as we already warned
            const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024;
            if (total > LARGE_FILE_THRESHOLD && !isSafari()) {
                setIsLargeFile(true);
            }

            const reader = response.body.getReader();
            const writer = fileStream.getWriter();

            const pump = async () => {
                const timeoutMs = 30000;
                let timeoutId;

                const readWithTimeout = () => {
                    return new Promise((resolve, reject) => {
                        timeoutId = setTimeout(() => {
                            reject(new Error("Download stalled"));
                        }, timeoutMs);

                        reader
                            .read()
                            .then((result) => {
                                clearTimeout(timeoutId);
                                resolve(result);
                            })
                            .catch((err) => {
                                clearTimeout(timeoutId);
                                reject(err);
                            });
                    });
                };

                try {
                    const { done, value } = await readWithTimeout();

                    if (done) {
                        await writer.close();
                        setDownloading(false);
                        setDownloadProgress(100);
                        setIsLargeFile(false);
                        setSafariWarning(false);
                        window.removeEventListener(
                            "beforeunload",
                            handleBeforeUnload,
                        );
                        return;
                    }

                    loaded += value.length;
                    if (total > 0) {
                        setDownloadProgress(Math.round((loaded / total) * 100));
                    }

                    await writer.write(value);
                    return pump();
                } catch (error) {
                    clearTimeout(timeoutId);
                    throw error;
                }
            };

            await pump();
        } catch (err) {
            console.error("Download error:", err);
            setDownloading(false);
            setIsLargeFile(false);
            setSafariWarning(false);
            window.removeEventListener("beforeunload", handleBeforeUnload);

            // Check for password-related errors
            if (
                err.code === "functions/permission-denied" ||
                err.message?.includes("Incorrect password")
            ) {
                setPasswordError("Incorrect password");
            } else if (err.code === "functions/failed-precondition") {
                if (err.message?.includes("expired")) {
                    setError("expired");
                } else if (err.message?.includes("Password is required")) {
                    setPasswordError("Password is required");
                } else {
                    setError("download_error");
                    setErrorMessage("An error occurred. Please try again.");
                }
            } else if (err.message?.includes("stalled")) {
                setError("download_error");
                let msg =
                    "Download stalled - please check your connection and try again.";
                if (isSafari()) {
                    msg +=
                        " Safari has known issues with large downloads - please try Chrome or Firefox.";
                }
                setErrorMessage(msg);
            } else if (err.name === "AbortError") {
                setError("download_error");
                setErrorMessage("Download was cancelled.");
            } else if (!navigator.onLine) {
                setError("download_error");
                setErrorMessage("Download failed - you appear to be offline.");
            } else {
                setError("download_error");
                let msg = "Download failed. Please try again.";
                if (isSafari()) {
                    msg +=
                        " Safari has known issues with large downloads - please try Chrome or Firefox.";
                }
                setErrorMessage(msg);
            }
        }
    }

    // Loading state
    if (loading) {
        return (
            <>
                <SimpleNavBar />
                <Container className={classes.container}>
                    <Paper className={classes.paper} elevation={3}>
                        <CircularProgress />
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            Loading...
                        </Typography>
                    </Paper>
                </Container>
                <ToolbarWithLogo />
            </>
        );
    }

    // Error states
    if (error === "not_found") {
        return (
            <>
                <SimpleNavBar />
                <Container className={classes.container}>
                    <Paper className={classes.paper} elevation={3}>
                        <ErrorOutlineIcon className={classes.errorIcon} />
                        <Typography variant="h6" gutterBottom>
                            Link Not Found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            This share link doesn't exist or has been removed.
                        </Typography>
                    </Paper>
                </Container>
                <ToolbarWithLogo />
            </>
        );
    }

    if (error === "expired") {
        return (
            <>
                <SimpleNavBar />
                <Container className={classes.container}>
                    <Paper className={classes.paper} elevation={3}>
                        <ErrorOutlineIcon className={classes.errorIcon} />
                        <Typography variant="h6" gutterBottom>
                            Link Expired
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            This share link has expired and is no longer
                            available.
                        </Typography>
                    </Paper>
                </Container>
                <ToolbarWithLogo />
            </>
        );
    }

    if (error === "fetch_error") {
        return (
            <>
                <SimpleNavBar />
                <Container className={classes.container}>
                    <Paper className={classes.paper} elevation={3}>
                        <ErrorOutlineIcon className={classes.errorIcon} />
                        <Typography variant="h6" gutterBottom>
                            Something Went Wrong
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            An error occurred. Please try again later.
                        </Typography>
                    </Paper>
                </Container>
                <ToolbarWithLogo />
            </>
        );
    }

    if (error === "download_error") {
        return (
            <>
                <SimpleNavBar />
                <Container className={classes.container}>
                    <Paper className={classes.paper} elevation={3}>
                        <ErrorOutlineIcon className={classes.errorIcon} />
                        <Typography variant="h6" gutterBottom>
                            Download Failed
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mb: 2 }}
                        >
                            {errorMessage ||
                                "An error occurred. Please try again."}
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setError(null);
                                setErrorMessage("");
                            }}
                        >
                            Try Again
                        </Button>
                    </Paper>
                </Container>
                <ToolbarWithLogo />
            </>
        );
    }

    // Success state - show file info and download
    return (
        <>
            <SimpleNavBar />
            <Container className={classes.container}>
                <Paper className={classes.paper} elevation={3}>
                    {shareLink.hasPassword ? (
                        <LockIcon className={classes.icon} color="primary" />
                    ) : (
                        <DownloadIcon
                            className={classes.icon}
                            color="primary"
                        />
                    )}

                    <Typography variant="h6" className={classes.fileName}>
                        {shareLink.fileName}
                    </Typography>

                    {!isNeverExpires(shareLink.expiresAt) && (
                        <Typography
                            variant="body2"
                            className={classes.expiryText}
                        >
                            Expires:{" "}
                            {dayjs(shareLink.expiresAt).format(
                                "DD MMM YYYY [at] HH:mm",
                            )}
                        </Typography>
                    )}

                    {shareLink.hasPassword && (
                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordError("");
                            }}
                            error={!!passwordError}
                            helperText={passwordError}
                            disabled={downloading}
                            className={classes.passwordField}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? (
                                                <VisibilityOff />
                                            ) : (
                                                <Visibility />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownload}
                        disabled={
                            downloading || (shareLink.hasPassword && !password)
                        }
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        {downloading ? "Downloading..." : "Download"}
                    </Button>

                    {downloading && (
                        <Box className={classes.progressContainer}>
                            <LinearProgress
                                variant={
                                    downloadProgress > 0
                                        ? "determinate"
                                        : "indeterminate"
                                }
                                value={downloadProgress}
                            />
                            {downloadProgress > 0 && (
                                <Typography
                                    variant="caption"
                                    sx={{ mt: 1, display: "block" }}
                                >
                                    {downloadProgress}%
                                </Typography>
                            )}
                        </Box>
                    )}

                    {isLargeFile && downloading && (
                        <Typography className={classes.warningText}>
                            Large file - please keep this tab open and visible
                            during download
                        </Typography>
                    )}

                    {safariWarning && downloading && (
                        <Typography className={classes.warningText}>
                            Large file on Safari - download may fail. For best
                            results, use Chrome or Firefox.
                        </Typography>
                    )}
                </Paper>
            </Container>
            <ToolbarWithLogo />
        </>
    );
}
